import { useCallback, useEffect, useRef, useState } from 'react';

export type AutoSaveState = 'idle' | 'saving' | 'saved' | 'error';

interface UseAutoSaveResult<T> {
  state: AutoSaveState;
  schedule: (value: T) => void;
  /** Grava agora o que estiver pendente. Usado antes de trocar de etapa. */
  flush: () => Promise<void>;
}

/**
 * Salvamento automático com espera.
 *
 * O rascunho é gravado depois que a pessoa para de digitar, não a cada tecla:
 * sem isso, um campo de 500 caracteres viraria 500 requisições.
 */
export function useAutoSave<T>(
  save: (value: T) => Promise<unknown>,
  delayMs = 800,
): UseAutoSaveResult<T> {
  const [state, setState] = useState<AutoSaveState>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<T | null>(null);
  const saveRef = useRef(save);

  useEffect(() => {
    saveRef.current = save;
  }, [save]);

  const write = useCallback(async (value: T) => {
    setState('saving');
    try {
      await saveRef.current(value);
      pending.current = null;
      setState('saved');
    } catch {
      setState('error');
    }
  }, []);

  const schedule = useCallback(
    (value: T) => {
      pending.current = value;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => write(value), delayMs);
    },
    [delayMs, write],
  );

  const flush = useCallback(async () => {
    if (timer.current) clearTimeout(timer.current);
    if (pending.current !== null) await write(pending.current);
  }, [write]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return { state, schedule, flush };
}
